import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Eye, CheckCircle, XCircle, Trash2, Plus, Save, Settings, Package, Clock } from "lucide-react";
import Paginator from "../../components/Paginator";

export default function AdminPTC() {
  const [ads, setAds] = useState([]);
  const [adsPage, setAdsPage] = useState(1);
  const [adsPerPage, setAdsPerPage] = useState(25);
  const [adsPagination, setAdsPagination] = useState({ total: 0, total_pages: 1 });
  const [settings, setSettings] = useState({});
  const [viewPkgs, setViewPkgs] = useState([]);
  const [timerPkgs, setTimerPkgs] = useState([]);
  const [tab, setTab] = useState("ads");
  const [loading, setLoading] = useState("");
  const [settingsForm, setSettingsForm] = useState({});

  useEffect(() => {
    API.get("/admin/ptc/ads", { params: { page: adsPage, per_page: adsPerPage } })
      .then(r => { setAds(r.data.ads || []); setAdsPagination({ total: r.data.total || 0, total_pages: r.data.total_pages || 1 }); })
      .catch(() => {});
  }, [adsPage, adsPerPage]);

  useEffect(() => {
    API.get("/admin/ptc/settings")
      .then(r => { setSettings(r.data.settings); setSettingsForm(r.data.settings); setViewPkgs(r.data.view_packages); setTimerPkgs(r.data.timer_packages); })
      .catch(() => {});
  }, []);

  const adAction = async (adId, action) => {
    try {
      await API.post(`/admin/ptc/ads/${adId}/action`, { action });
      toast.success(`Ad ${action}d`);
      setAds(prev => action === "delete" ? prev.filter(a => a.ad_id !== adId) : prev.map(a => a.ad_id === adId ? { ...a, status: action === "approve" ? "active" : "rejected" } : a));
    } catch { toast.error("Failed"); }
  };

  const saveSettings = async () => {
    setLoading("settings");
    try { await API.put("/admin/ptc/settings", settingsForm); toast.success("Saved"); } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const createViewPkg = async () => {
    try {
      await API.post("/admin/ptc/view-packages", { views: 1000, price: 100 });
      toast.success("Created");
      const { data } = await API.get("/admin/ptc/settings");
      setViewPkgs(data.view_packages);
    } catch { toast.error("Failed"); }
  };

  const createTimerPkg = async () => {
    try {
      await API.post("/admin/ptc/timer-packages", { seconds: 15, multiplier: 1.5, label: "15 seconds" });
      toast.success("Created");
      const { data } = await API.get("/admin/ptc/settings");
      setTimerPkgs(data.timer_packages);
    } catch { toast.error("Failed"); }
  };

  const updateViewPkg = async (pkgId, field, value) => {
    try {
      await API.put(`/admin/ptc/view-packages/${pkgId}`, { [field]: value });
      setViewPkgs(prev => prev.map(p => p.package_id === pkgId ? { ...p, [field]: value } : p));
    } catch { toast.error("Failed"); }
  };

  const updateTimerPkg = async (pkgId, field, value) => {
    try {
      await API.put(`/admin/ptc/timer-packages/${pkgId}`, { [field]: value });
      setTimerPkgs(prev => prev.map(p => p.package_id === pkgId ? { ...p, [field]: value } : p));
    } catch { toast.error("Failed"); }
  };

  const pending = ads.filter(a => a.status === "pending");
  const STATUS_COLOR = { active: "#10B981", pending: "#F59E0B", rejected: "#EF4444", completed: "#64748B", paused: "#3B82F6" };
  const [editingAdId, setEditingAdId] = useState(null);
  const [adEditForm, setAdEditForm] = useState({});

  const adminEditAd = async (adId) => {
    setLoading(`edit-${adId}`);
    try {
      await API.put(`/admin/ptc/ads/${adId}/edit`, adEditForm);
      toast.success("Ad updated");
      setEditingAdId(null);
      const { data } = await API.get("/admin/ptc/ads");
      setAds(data.ads);
    } catch { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  return (
    <AdminLayout title="PTC Ads Management">
      <div className="space-y-4" data-testid="admin-ptc">
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40">
          {[["ads", `Ads ${pending.length > 0 ? `(${pending.length})` : ""}`], ["packages", "Packages"], ["settings", "Settings"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md ${tab === key ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B]"}`}>{label}</button>
          ))}
        </div>

        {/* ADS */}
        {tab === "ads" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-2 text-xs text-[#64748B]">
              <span>Total: {adsPagination.total}</span><span>|</span><span className="text-[#F59E0B]">Pending: {pending.length}</span><span>|</span>
              <span className="text-[#FFD600]">Platform earnings: {(settings.total_platform_earnings || 0).toFixed(0)}c</span>
            </div>
            {ads.map(ad => {
              const sc = STATUS_COLOR[ad.status] || "#64748B";
              const isEditing = editingAdId === ad.ad_id;
              return (
              <div key={ad.ad_id} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 overflow-hidden" data-testid={`ptc-admin-${ad.ad_id}`}>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-[#F8FAFC]">{ad.title}</p>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: `${sc}15`, color: sc }}>{ad.status}</span>
                      </div>
                      <p className="text-xs text-[#94A3B8] truncate">{ad.target_url}</p>
                      <p className="text-xs text-[#64748B]">{ad.total_views_delivered || 0}/{ad.total_views} views | {ad.remaining_views} left | {ad.timer_seconds}s | {ad.reward_coins}c/view | by {ad.advertiser_name} | fee: {ad.platform_fee || 0}c</p>
                    </div>
                    <div className="flex items-center gap-1 ml-3 flex-wrap">
                      {ad.status === "pending" && (<>
                        <button onClick={() => adAction(ad.ad_id, "approve")} className="p-1.5 text-[#10B981] hover:bg-[#10B981]/10 rounded"><CheckCircle className="w-4 h-4" /></button>
                        <button onClick={() => adAction(ad.ad_id, "reject")} className="p-1.5 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"><XCircle className="w-4 h-4" /></button>
                      </>)}
                      {ad.status === "active" && <button onClick={() => adAction(ad.ad_id, "pause")} className="px-2 py-1 text-[9px] font-bold uppercase text-[#3B82F6] border border-[#3B82F6]/30 rounded hover:bg-[#3B82F6]/10">Pause</button>}
                      {ad.status === "paused" && <button onClick={() => adAction(ad.ad_id, "resume")} className="px-2 py-1 text-[9px] font-bold uppercase text-[#10B981] border border-[#10B981]/30 rounded hover:bg-[#10B981]/10">Resume</button>}
                      <button onClick={() => { setEditingAdId(isEditing ? null : ad.ad_id); setAdEditForm({ title: ad.title, description: ad.description, target_url: ad.target_url, status: ad.status, remaining_views: ad.remaining_views, reward_coins: ad.reward_coins }); }}
                        className="px-2 py-1 text-[9px] font-bold uppercase text-[#00F0FF] border border-[#00F0FF]/30 rounded hover:bg-[#00F0FF]/10">Edit</button>
                      <button onClick={() => adAction(ad.ad_id, "delete")} className="p-1.5 text-[#64748B] hover:text-[#EF4444] rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
                {isEditing && (
                  <div className="px-4 pb-4 border-t border-[#00F0FF]/20 animate-slide-up">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                      <div><Label className="text-[#64748B] text-[9px]">Title</Label><Input value={adEditForm.title || ""} onChange={(e) => setAdEditForm({...adEditForm, title: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-7 text-xs mt-0.5" /></div>
                      <div><Label className="text-[#64748B] text-[9px]">URL</Label><Input value={adEditForm.target_url || ""} onChange={(e) => setAdEditForm({...adEditForm, target_url: e.target.value})} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] h-7 text-xs mt-0.5" /></div>
                      <div><Label className="text-[#64748B] text-[9px]">Status</Label><select value={adEditForm.status || ""} onChange={(e) => setAdEditForm({...adEditForm, status: e.target.value})} className="w-full h-7 mt-0.5 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded px-1 text-xs"><option value="active">Active</option><option value="pending">Pending</option><option value="paused">Paused</option><option value="rejected">Rejected</option><option value="completed">Completed</option></select></div>
                      <div><Label className="text-[#64748B] text-[9px]">Remaining Views</Label><Input type="number" value={adEditForm.remaining_views || 0} onChange={(e) => setAdEditForm({...adEditForm, remaining_views: parseInt(e.target.value)})} className="bg-[#0B0E14] border-[#2A3241] text-[#FFD600] h-7 text-xs font-mono-game mt-0.5" /></div>
                      <div><Label className="text-[#64748B] text-[9px]">Reward/View</Label><Input type="number" value={adEditForm.reward_coins || 0} onChange={(e) => setAdEditForm({...adEditForm, reward_coins: parseInt(e.target.value)})} className="bg-[#0B0E14] border-[#2A3241] text-[#00F0FF] h-7 text-xs font-mono-game mt-0.5" /></div>
                    </div>
                    <button onClick={() => adminEditAd(ad.ad_id)} disabled={loading === `edit-${ad.ad_id}`} className="mt-2 px-3 py-1.5 text-[10px] font-bold uppercase bg-[#10B981] text-white rounded-lg disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" />Save</button>
                  </div>
                )}
              </div>
              );
            })}
            {ads.length === 0 && <p className="text-[#94A3B8] text-center py-8">No PTC ads submitted yet</p>}
            <Paginator page={adsPage} totalPages={adsPagination.total_pages} total={adsPagination.total}
              perPage={adsPerPage} onPerPageChange={(n) => { setAdsPerPage(n); setAdsPage(1); }}
              onPageChange={setAdsPage} testIdPrefix="admin-ptc-pager" />
          </div>
        )}

        {/* PACKAGES */}
        {tab === "packages" && (
          <div className="space-y-6">
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Package className="w-4 h-4 text-[#00F0FF]" /><h3 className="text-sm font-bold text-[#F8FAFC]">View Packages</h3></div>
                <button onClick={createViewPkg} className="px-3 py-1.5 text-[10px] font-bold uppercase bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/30 rounded-lg"><Plus className="w-3 h-3 inline mr-1" />Add</button>
              </div>
              <div className="space-y-2">
                {viewPkgs.map(p => (
                  <div key={p.package_id} className="flex items-center gap-3 bg-[#0B0E14] rounded-lg p-3">
                    <Input type="number" value={p.views} onChange={(e) => { setViewPkgs(prev => prev.map(x => x.package_id === p.package_id ? { ...x, views: parseInt(e.target.value) } : x)); }}
                      onBlur={(e) => updateViewPkg(p.package_id, "views", parseInt(e.target.value))} className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-8 w-24 text-xs font-mono-game" />
                    <span className="text-xs text-[#64748B]">views</span>
                    <Input type="number" value={p.price} onChange={(e) => { setViewPkgs(prev => prev.map(x => x.package_id === p.package_id ? { ...x, price: parseInt(e.target.value) } : x)); }}
                      onBlur={(e) => updateViewPkg(p.package_id, "price", parseInt(e.target.value))} className="bg-[#131722] border-[#2A3241] text-[#FFD600] h-8 w-24 text-xs font-mono-game" />
                    <span className="text-xs text-[#64748B]">coins</span>
                    <button onClick={() => { API.delete(`/admin/ptc/view-packages/${p.package_id}`); setViewPkgs(prev => prev.filter(x => x.package_id !== p.package_id)); }}
                      className="p-1 text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#B026FF]" /><h3 className="text-sm font-bold text-[#F8FAFC]">Timer Packages</h3></div>
                <button onClick={createTimerPkg} className="px-3 py-1.5 text-[10px] font-bold uppercase bg-[#B026FF]/10 text-[#B026FF] border border-[#B026FF]/30 rounded-lg"><Plus className="w-3 h-3 inline mr-1" />Add</button>
              </div>
              <div className="space-y-2">
                {timerPkgs.map(p => (
                  <div key={p.package_id} className="flex items-center gap-3 bg-[#0B0E14] rounded-lg p-3">
                    <Input type="number" value={p.seconds} onChange={(e) => { setTimerPkgs(prev => prev.map(x => x.package_id === p.package_id ? { ...x, seconds: parseInt(e.target.value) } : x)); }}
                      onBlur={(e) => updateTimerPkg(p.package_id, "seconds", parseInt(e.target.value))} className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-8 w-20 text-xs font-mono-game" />
                    <span className="text-xs text-[#64748B]">sec</span>
                    <Input type="number" step="0.1" value={p.multiplier} onChange={(e) => { setTimerPkgs(prev => prev.map(x => x.package_id === p.package_id ? { ...x, multiplier: parseFloat(e.target.value) } : x)); }}
                      onBlur={(e) => updateTimerPkg(p.package_id, "multiplier", parseFloat(e.target.value))} className="bg-[#131722] border-[#2A3241] text-[#B026FF] h-8 w-20 text-xs font-mono-game" />
                    <span className="text-xs text-[#64748B]">×</span>
                    <Input value={p.label} onChange={(e) => { setTimerPkgs(prev => prev.map(x => x.package_id === p.package_id ? { ...x, label: e.target.value } : x)); }}
                      onBlur={(e) => updateTimerPkg(p.package_id, "label", e.target.value)} className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-8 flex-1 text-xs" />
                    <button onClick={() => { API.delete(`/admin/ptc/timer-packages/${p.package_id}`); setTimerPkgs(prev => prev.filter(x => x.package_id !== p.package_id)); }}
                      className="p-1 text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* SETTINGS */}
        {tab === "settings" && (
          <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4">
            <div className="flex items-center gap-2"><Settings className="w-5 h-5 text-[#FFD600]" /><h3 className="text-lg font-bold text-[#F8FAFC]">PTC Settings</h3></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#94A3B8] text-sm">View Cooldown (hours)</Label>
                <Input type="number" value={settingsForm.view_cooldown_hours || 24} onChange={(e) => setSettingsForm({ ...settingsForm, view_cooldown_hours: parseInt(e.target.value) })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-sm">Max Claims/Minute</Label>
                <Input type="number" value={settingsForm.max_claims_per_minute || 5} onChange={(e) => setSettingsForm({ ...settingsForm, max_claims_per_minute: parseInt(e.target.value) })} className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
              </div>
              <div>
                <Label className="text-[#94A3B8] text-sm">New Campaign Fee (%)</Label>
                <Input type="number" value={settingsForm.new_campaign_fee ?? settingsForm.platform_fee_pct ?? 13} onChange={(e) => setSettingsForm({ ...settingsForm, new_campaign_fee: parseInt(e.target.value) })} className="bg-[#0B0E14] border-[#2A3241] text-[#FFD600] mt-1 font-mono-game" />
                <p className="text-[9px] text-[#64748B] mt-0.5">% added ON TOP of base cost for new ad campaigns</p>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-sm">Recharge Campaign Fee (%)</Label>
                <Input type="number" value={settingsForm.recharge_campaign_fee ?? settingsForm.recharge_fee_pct ?? 10} onChange={(e) => setSettingsForm({ ...settingsForm, recharge_campaign_fee: parseInt(e.target.value) })} className="bg-[#0B0E14] border-[#2A3241] text-[#FFD600] mt-1 font-mono-game" />
                <p className="text-[9px] text-[#64748B] mt-0.5">% added ON TOP of recharge cost</p>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-sm">Total Platform Earnings</Label>
                <div className="flex items-center gap-3 mt-1">
                  <p className="font-mono-game text-lg font-bold text-[#FFD600]">{(settings.total_platform_earnings || 0).toFixed(2)} coins</p>
                  <button onClick={async () => { try { await API.put("/admin/ptc/settings", { total_platform_earnings: 0 }); toast.success("Earnings reset to 0"); setSettings(prev => ({ ...prev, total_platform_earnings: 0 })); } catch { toast.error("Failed"); } }}
                    className="px-3 py-1.5 text-[10px] font-bold uppercase text-[#EF4444] border border-[#EF4444]/30 rounded-lg hover:bg-[#EF4444]/10">
                    Reset to 0
                  </button>
                </div>
                <p className="text-[9px] text-[#64748B]">Accumulated from ad purchases. Reset to track weekly/monthly.</p>
              </div>
              <div>
                <Label className="text-[#94A3B8] text-sm">CAPTCHA</Label>
                <button onClick={() => setSettingsForm({ ...settingsForm, captcha_enabled: !settingsForm.captcha_enabled })}
                  className={`w-full h-10 mt-1 text-sm font-bold rounded-lg border ${settingsForm.captcha_enabled ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-[#2A3241] text-[#64748B]"}`}>
                  {settingsForm.captcha_enabled ? "ENABLED" : "DISABLED"}
                </button>
              </div>
              {settingsForm.captcha_enabled && (
                <div>
                  <Label className="text-[#94A3B8] text-sm">CAPTCHA Type</Label>
                  <select value={settingsForm.captcha_type || "random_math"} onChange={(e) => setSettingsForm({ ...settingsForm, captcha_type: e.target.value })}
                    className="w-full h-10 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                    <option value="random_math">Random Math</option>
                    <option value="static_math">Static Math</option>
                    <option value="recaptcha">Google reCAPTCHA</option>
                  </select>
                </div>
              )}
            </div>
            <button onClick={saveSettings} disabled={loading === "settings"} className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {loading === "settings" ? "Saving..." : "Save Settings"}
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
